import React from 'react'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar'
import { Separator } from '../ui/separator'
import { 
  Download, 
  Globe, 
  Palette, 
  Type, 
  Image as ImageIcon,
  FileText,
  ArrowLeft,
  ExternalLink,
  Copy,
  Share2,
  Mail,
  Phone,
  MapPin
} from 'lucide-react'

interface Brand {
  id: string
  name: string
  description?: string
  website_url?: string
  is_public?: boolean
  slug?: string
  metadata?: {
    industry?: string
  }
}

interface PublicBrandPageProps {
  brand: Brand
  onBack?: () => void
  isPreview?: boolean
}

export function PublicBrandPage({ brand, onBack, isPreview = false }: PublicBrandPageProps) {
  // Mock brand data for demonstration
  const brandData = {
    tagline: "Innovative Technology Solutions for Modern Businesses",
    story: "Founded with a vision to transform how businesses leverage technology, our company has been at the forefront of digital innovation for over a decade. We believe in creating solutions that not only meet today's needs but anticipate tomorrow's challenges.",
    colors: [
      { name: "Primary Blue", hex: "#2563EB", usage: "Primary brand color for logos and CTAs" },
      { name: "Secondary Navy", hex: "#1E40AF", usage: "Supporting text and accent elements" },
      { name: "Accent Green", hex: "#10B981", usage: "Success states and highlights" },
      { name: "Light Gray", hex: "#F8FAFC", usage: "Background and subtle elements" },
      { name: "Dark Gray", hex: "#334155", usage: "Primary text and headings" }
    ],
    typography: {
      primary: "Inter",
      secondary: "Roboto",
      heading: "font-medium",
      body: "font-normal"
    },
    contact: {
      email: "hello@" + brand.name.toLowerCase().replace(/\s+/g, '') + ".com",
      phone: "+1 (555) 123-4567",
      address: "123 Innovation Street, Tech City, TC 12345"
    },
    assets: [
      { name: "Logo Package", type: "ZIP", description: "Complete logo set with variations" },
      { name: "Brand Guidelines", type: "PDF", description: "Comprehensive brand usage guide" },
      { name: "Color Palette", type: "ASE", description: "Adobe Swatch Exchange file" },
      { name: "Typography Kit", type: "ZIP", description: "Font files and usage examples" }
    ]
  }

  const handleCopyUrl = () => {
    const url = window.location.href
    navigator.clipboard.writeText(url)
  }

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: `${brand.name} Brand Guidelines`,
        text: brandData.tagline,
        url: window.location.href
      })
    }
  }

  const handleDownloadAsset = (assetName: string) => {
    // Mock download functionality
    console.log(`Downloading ${assetName}`)
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {onBack && (
                <>
                  <Button variant="ghost" size="sm" onClick={onBack}>
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                  <div className="h-6 w-px bg-slate-300" />
                </>
              )}
              <div className="flex items-center space-x-3">
                <Avatar className="h-12 w-12">
                  <AvatarImage src="" alt={brand.name} />
                  <AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-500 text-white font-semibold">
                    {brand.name.substring(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <div className="flex items-center gap-2">
                    <h1 className="text-xl font-bold">{brand.name}</h1>
                    {isPreview && (
                      <Badge variant="secondary" className="bg-orange-100 text-orange-700">
                        Preview
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-slate-600">{brandData.tagline}</p>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <Button variant="outline" size="sm" onClick={handleCopyUrl}>
                <Copy className="w-4 h-4 mr-2" />
                Copy URL
              </Button>
              <Button variant="outline" size="sm" onClick={handleShare}>
                <Share2 className="w-4 h-4 mr-2" />
                Share
              </Button>
              {brand.website_url && (
                <Button variant="outline" size="sm" asChild>
                  <a href={brand.website_url} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Visit Website
                  </a>
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Brand Story */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Brand Story
                </CardTitle>
                <CardDescription>
                  Learn about our mission, vision, and values
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-slate-700 leading-relaxed mb-4">
                  {brandData.story}
                </p>
                {brand.metadata?.industry && (
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary">{brand.metadata.industry}</Badge>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Color Palette */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Palette className="w-5 h-5" />
                  Color Palette
                </CardTitle>
                <CardDescription>
                  Official brand colors and their usage guidelines
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {brandData.colors.map((color, index) => (
                    <div key={index} className="flex items-center gap-3 p-3 border rounded-lg">
                      <div 
                        className="w-12 h-12 rounded-lg border"
                        style={{ backgroundColor: color.hex }}
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{color.name}</span>
                          <code className="text-xs bg-slate-100 px-2 py-1 rounded">{color.hex}</code>
                        </div>
                        <p className="text-xs text-slate-600 mt-1">{color.usage}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Typography */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Type className="w-5 h-5" />
                  Typography
                </CardTitle>
                <CardDescription>
                  Font families and typography guidelines
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div>
                    <h3 className="font-medium mb-2">Primary Font: {brandData.typography.primary}</h3>
                    <div className="space-y-2">
                      <div className="text-2xl font-medium" style={{ fontFamily: brandData.typography.primary }}>
                        The quick brown fox jumps over the lazy dog
                      </div>
                      <div className="text-lg" style={{ fontFamily: brandData.typography.primary }}>
                        The quick brown fox jumps over the lazy dog
                      </div>
                      <div className="text-base" style={{ fontFamily: brandData.typography.primary }}>
                        The quick brown fox jumps over the lazy dog
                      </div>
                    </div>
                  </div>
                  
                  <Separator />
                  
                  <div>
                    <h3 className="font-medium mb-2">Secondary Font: {brandData.typography.secondary}</h3>
                    <div className="space-y-2">
                      <div className="text-lg" style={{ fontFamily: brandData.typography.secondary }}>
                        Used for body text and supporting content
                      </div>
                      <div className="text-base" style={{ fontFamily: brandData.typography.secondary }}>
                        The quick brown fox jumps over the lazy dog
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Logo Guidelines */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ImageIcon className="w-5 h-5" />
                  Logo Guidelines
                </CardTitle>
                <CardDescription>
                  Logo usage, variations, and spacing requirements
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="text-center p-8 bg-white border-2 border-dashed border-slate-300 rounded-lg">
                    <Avatar className="h-16 w-16 mx-auto mb-4">
                      <AvatarImage src="" alt={brand.name} />
                      <AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-500 text-white font-semibold text-lg">
                        {brand.name.substring(0, 2).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <p className="text-sm text-slate-600">Primary Logo</p>
                  </div>
                  
                  <div className="text-center p-8 bg-slate-900 border-2 border-dashed border-slate-600 rounded-lg">
                    <Avatar className="h-16 w-16 mx-auto mb-4">
                      <AvatarImage src="" alt={brand.name} />
                      <AvatarFallback className="bg-white text-slate-900 font-semibold text-lg">
                        {brand.name.substring(0, 2).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <p className="text-sm text-slate-400">Reversed Logo</p>
                  </div>
                </div>
                
                <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h4 className="font-medium text-blue-900 mb-2">Usage Guidelines</h4>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>• Maintain minimum clear space equal to half the logo height</li>
                    <li>• Minimum size: 24px for digital, 0.5 inches for print</li>
                    <li>• Do not stretch, skew, or alter proportions</li>
                    <li>• Use only approved color variations</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Download Assets */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Download className="w-5 h-5" />
                  Download Assets
                </CardTitle>
                <CardDescription>
                  Get the complete brand asset package
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {brandData.assets.map((asset, index) => (
                  <Button 
                    key={index}
                    variant="outline" 
                    className="w-full justify-start"
                    onClick={() => handleDownloadAsset(asset.name)}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    <div className="text-left">
                      <div className="font-medium">{asset.name}</div>
                      <div className="text-xs text-slate-500">{asset.type} • {asset.description}</div>
                    </div>
                  </Button>
                ))}
              </CardContent>
            </Card>

            {/* Contact Information */}
            <Card>
              <CardHeader>
                <CardTitle>Contact Information</CardTitle>
                <CardDescription>
                  Get in touch with our team
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  <Mail className="w-4 h-4 text-slate-500" />
                  <a href={`mailto:${brandData.contact.email}`} className="text-blue-600 hover:underline">
                    {brandData.contact.email}
                  </a>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="w-4 h-4 text-slate-500" />
                  <a href={`tel:${brandData.contact.phone}`} className="text-blue-600 hover:underline">
                    {brandData.contact.phone}
                  </a>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 text-slate-500 mt-0.5" />
                  <span className="text-slate-700">{brandData.contact.address}</span>
                </div>
              </CardContent>
            </Card>

            {/* Brand Stats */}
            <Card>
              <CardHeader>
                <CardTitle>Brand Statistics</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-sm text-slate-600">Page Views</span>
                  <span className="font-medium">1,247</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-slate-600">Asset Downloads</span>
                  <span className="font-medium">89</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-slate-600">Last Updated</span>
                  <span className="font-medium">Dec 2024</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200 mt-16">
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-600">Powered by</span>
              <div className="flex items-center gap-1">
                <div className="w-5 h-5 bg-gradient-to-br from-blue-600 to-purple-600 rounded flex items-center justify-center">
                  <Globe className="w-3 h-3 text-white" />
                </div>
                <span className="font-semibold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  BrandZit
                </span>
              </div>
            </div>
            <div className="text-sm text-slate-500">
              © 2024 {brand.name}. All rights reserved.
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}